import DOMPurify from 'dompurify'

export interface ProcessedContent {
  html: string
  plainText: string
  links: string[]
  images: string[]
  videos: string[]
  documents: string[]
}

/**
 * Processes raw HTML content from WordPress and sanitizes it
 */
export const processHTMLContent = (htmlContent: string): ProcessedContent => {
  // Sanitize HTML content
  const cleanHTML = DOMPurify.sanitize(htmlContent, {
    ALLOWED_TAGS: [
      'p',
      'br',
      'strong',
      'em',
      'u',
      'h1',
      'h2',
      'h3',
      'h4',
      'h5',
      'h6',
      'ul',
      'ol',
      'li',
      'a',
      'img',
      'video',
      'figure',
      'figcaption',
      'blockquote',
      'div',
      'span',
      'hr',
      'table',
      'thead',
      'tbody',
      'tr',
      'td',
      'th'
    ],
    ALLOWED_ATTR: [
      'href',
      'src',
      'alt',
      'title',
      'class',
      'id',
      'target',
      'rel',
      'width',
      'height',
      'controls',
      'style'
    ]
  })

  // Create a temporary DOM element to parse content
  const tempDiv = document.createElement('div')
  tempDiv.innerHTML = cleanHTML

  // Extract different types of content
  const links = Array.from(tempDiv.querySelectorAll('a[href]'))
    .map((link) => (link as HTMLAnchorElement).href)
    .filter((href) => href && !href.startsWith('#'))

  const images = Array.from(tempDiv.querySelectorAll('img[src]')).map(
    (img) => (img as HTMLImageElement).src
  )

  const videos = Array.from(
    tempDiv.querySelectorAll(
      'video[src], iframe[src*="youtube"], iframe[src*="vimeo"]'
    )
  ).map((video) => (video as HTMLVideoElement | HTMLIFrameElement).src)

  const documents = links.filter(
    (link) =>
      link.endsWith('.pdf') ||
      link.endsWith('.doc') ||
      link.endsWith('.docx') ||
      link.endsWith('.ppt') ||
      link.endsWith('.pptx') ||
      link.endsWith('.xlsx') ||
      link.endsWith('.xls')
  )

  // Extract plain text
  const plainText = tempDiv.textContent || tempDiv.innerText || ''

  return {
    html: cleanHTML,
    plainText,
    links,
    images,
    videos,
    documents
  }
}

/**
 * Transforms WordPress-style classes to our Tailwind classes
 */
export const transformWordPressClasses = (html: string): string => {
  return (
    html
      // Remove WordPress-specific classes and replace with Tailwind
      .replace(/class="wp-block-group[^"]*"/g, 'class="mb-8"')
      .replace(
        /class="wp-block-columns[^"]*"/g,
        'class="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8"'
      )
      .replace(/class="wp-block-column[^"]*"/g, 'class="space-y-4"')
      .replace(
        /class="wp-block-separator[^"]*"/g,
        'class="border-t border-[#9298a8] my-8"'
      )
      .replace(
        /class="wp-block-pullquote[^"]*"/g,
        'class="border-l-4 border-[#A6410A] pl-6 my-8 italic text-lg text-[#713d43]"'
      )
      .replace(/class="wp-block-image[^"]*"/g, 'class="mb-6"')
      .replace(/class="wp-block-video[^"]*"/g, 'class="mb-6"')
      .replace(/class="wp-block-file[^"]*"/g, 'class="mb-4"')
      .replace(/class="wp-block-embed[^"]*"/g, 'class="mb-6"')
      .replace(
        /class="wp-block-buttons[^"]*"/g,
        'class="flex flex-wrap gap-4 mb-6"'
      )
      .replace(/class="wp-block-button[^"]*"/g, 'class=""')
      .replace(
        /class="wp-block-button__link[^"]*"/g,
        'class="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-[#A6410A] hover:bg-[#894d35] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#A6410A] transition-colors duration-200"'
      )

      // Transform WordPress alignment classes
      .replace(/alignwide/g, 'w-full max-w-6xl mx-auto')
      .replace(/alignfull/g, 'w-full')
      .replace(/aligncenter/g, 'mx-auto text-center')
      .replace(/alignleft/g, 'float-left mr-4')
      .replace(/alignright/g, 'float-right ml-4')

      // Transform WordPress text alignment
      .replace(/has-text-align-center/g, 'text-center')
      .replace(/has-text-align-left/g, 'text-left')
      .replace(/has-text-align-right/g, 'text-right')

      // Transform WordPress font sizes to our scale
      .replace(/has-large-font-size/g, 'text-lg md:text-xl')
      .replace(/has-larger-font-size/g, 'text-xl md:text-2xl')
      .replace(/has-normal-font-size/g, 'text-base')
      .replace(/has-small-font-size/g, 'text-sm')

      // Transform WordPress colors to our palette
      .replace(/has-primary-color/g, 'text-[#3C2416]')
      .replace(/has-accent-color/g, 'text-[#A6410A]')
      .replace(/has-background-background-color/g, 'bg-[#EDE6DE]')
      .replace(/has-accent-background-color/g, 'bg-[#A6410A] text-white')
      .replace(/has-text-color/g, '')
      .replace(/has-background/g, 'p-4 rounded-lg')

      // Transform inline styles to Tailwind classes
      .replace(/style="font-size:\s*(\d+)px"/g, (match, size) => {
        const sizeNum = parseInt(size)
        if (sizeNum >= 32) return 'class="text-2xl md:text-3xl"'
        if (sizeNum >= 24) return 'class="text-xl md:text-2xl"'
        if (sizeNum >= 20) return 'class="text-lg md:text-xl"'
        if (sizeNum >= 18) return 'class="text-lg"'
        return 'class="text-base"'
      })

      // Transform drop caps
      .replace(
        /has-drop-cap/g,
        'first-letter:text-6xl first-letter:font-bold first-letter:text-[#A6410A] first-letter:float-left first-letter:mr-2 first-letter:mt-1'
      )

      // Clean up multiple classes and empty class attributes
      .replace(/class="([^"]*)\s+([^"]*)"/g, 'class="$1 $2"')
      .replace(/class=""/g, '')
      .replace(/class="\s+/g, 'class="')
      .replace(/\s+"/g, '"')
  )
}

/**
 * Extracts and formats pullquotes from content
 */
export const extractPullquotes = (html: string): string[] => {
  const tempDiv = document.createElement('div')
  tempDiv.innerHTML = html

  const pullquotes = Array.from(
    tempDiv.querySelectorAll('.wp-block-pullquote blockquote p')
  )
    .map((quote) => quote.textContent || '')
    .filter((text) => text.trim().length > 0)

  return pullquotes
}

/**
 * Converts WordPress button markup to our button classes
 */
export const transformButtons = (html: string): string => {
  return html.replace(
    /<a[^>]*class="[^"]*wp-block-button__link[^"]*"[^>]*>/g,
    (match) => {
      return match.replace(
        /class="[^"]*"/,
        'class="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-[#A6410A] hover:bg-[#894d35] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#A6410A] transition-colors duration-200"'
      )
    }
  )
}

/**
 * Main function to process all content - keeps it simple by just sanitizing and extracting metadata
 */
export const processToolkitContent = (
  htmlContent: string
): ProcessedContent => {
  // Just sanitize and extract metadata, don't transform styling
  const processed = processHTMLContent(htmlContent)

  // Apply minimal transformations for functionality
  let cleanHtml = processed.html
  cleanHtml = transformButtons(cleanHtml) // Transform buttons for better UX

  return {
    ...processed,
    html: cleanHtml
  }
}
